import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../../Api"
import { IndividualDto } from "../../types/Individual"
import UnsavedChangesGuard from "../../UnsavedChanges"
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup";
import * as regex from "../../constants/regex";
import * as css from "../../constants/css";
import FormInput from "../../controls/FormInput";
import { FormSelect } from "../../controls/FormSelect";

const formSchema = yup
  .object().shape({
    firstName: yup.string().required("First Name is required").max(100, "First Name must not exceed 100 characters"),
    middleName: yup.string().max(100, "Middle Name must not exceed 100 characters"),
    lastName: yup.string().required("Last Name is required").max(100, "Last Name must not exceed 100 characters"),
    nameSuffix: yup.string().max(50, "Suffix must not exceed 50 characters"),
    countryCode: yup.string().required("Country Code is required").matches(regex.countryCode, "Country Code must be 1-3 digits").max(3, "Country Code must not exceed 3 characters"),
    phoneNumber: yup.string().required("Phone Number is required").matches(regex.phoneNumber, "Phone Number must be 4-12 digits").max(20, "Phone Number must not exceed 20 characters"),
    email: yup.string().required("Email is required").matches(regex.email, "Must be a valid email").max(255, "Email must not exceed 255 characters"),
    taxpayerCountry: yup.string().required("Taxpayer Country is required").matches(/^[A-Z]{2}$/, "Taxpayer Country must be a valid 2-letter country code (e.g., US, CA, GB)").max(2, "Taxpayer Country must not exceed 2 characters"),
    dateOfBirth: yup.string().required("Date of Birth is required").test('is-valid-date', 'Date of Birth must be a valid date', function (value) {
      if (!value) return false;
      const date = new Date(value);
      if (isNaN(date.getTime())) return false;

      // Check if person is at least 18 years old
      const today = new Date();
      const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

      if (date > eighteenYearsAgo) {
        return this.createError({ message: 'You must be at least 18 years old' });
      }

      return true;
    }),
    ssn: yup.string().required("SSN is required").matches(/^\d{9}$/, "SSN must be exactly 9 digits"),
    maritalStatus: yup.string().required("Marital Status is required").max(30, "Marital Status must not exceed 30 characters"),
    dependents: yup.number().required("Dependents is required").integer("Dependents must be a whole number").transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      const num = Number(originalValue);
      return isNaN(num) ? originalValue : num;
    }),
    occupation: yup.string().max(100, "Occupation must not exceed 100 characters"),
    incomeSource: yup.string().max(100, "Income Source must not exceed 100 characters"),
    employerName: yup.string().max(100, "Employer Name must not exceed 100 characters"),
    employmentStatus: yup.string().max(30, "Employment Status must not exceed 30 characters"),

    addressLine1: yup.string().required("Address Line 1 is required").max(100, "Address Line 1 must not exceed 100 characters"),
    addressLine2: yup.string().max(100, "Address Line 2 must not exceed 100 characters"),
    addressCity: yup.string().required("City is required").max(100, "City must not exceed 100 characters"),
    addressState: yup.string().required("State is required").max(100, "State must not exceed 100 characters"),
    addressZipCode: yup.string().required("Zip Code is required").max(50, "Zip Code must not exceed 50 characters"),
    addressCountry: yup.string().required("Country is required").max(100, "Country must not exceed 100 characters"),

    employerAddressLine1: yup.string().max(100, "Employer Address Line 1 must not exceed 100 characters"),
    employerAddressLine2: yup.string().max(100, "Employer Address Line 2 must not exceed 100 characters"),
    employerAddressCity: yup.string().max(100, "Employer City must not exceed 100 characters"),
    employerAddressState: yup.string().max(100, "Employer State must not exceed 100 characters"),
    employerAddressZipCode: yup.string().max(50, "Employer Zip Code must not exceed 50 characters"),
    employerAddressCountry: yup.string().max(100, "Employer Country must not exceed 100 characters"),
  });

const OnboardingIndividual: React.FC = (route) => {
  const { user } = useAuth0();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [individual, setIndividual] = useState<IndividualDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notFoundError, setNotFoundError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [employmentStatus, setEmploymentStatus] = useState('');
  const { register, control, formState: { errors, isDirty }, handleSubmit } = useForm<IndividualDto>({
    //@ts-ignore
    resolver: yupResolver<IndividualDto>(formSchema)
  });

  // Function to convert date from MM/dd/yyyy to yyyy-MM-dd format for HTML date input
  const convertDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Returns yyyy-MM-dd format
  };

  const showEmployerInfo = employmentStatus === "Employed";

  useEffect(() => {
    const populateExistingData = async () => {
      try {
        setNotFoundError(null);
        const response = await api.get("/api/onboarding/Individual", { params: { email: user?.email } });
        if (response.data.individual) {
          // Convert date format for HTML date input
          const individualWithConvertedDate = {
            ...response.data.individual,
            dateOfBirth: convertDateForInput(response.data.individual.dateOfBirth)
          };

          setEmploymentStatus(individualWithConvertedDate.employmentStatus);
          setIndividual(individualWithConvertedDate);
        } else {
          setIndividual({} as IndividualDto)
          setLoading(false);
        }
      } catch (error: any) {
        if (error.response) {
          const status = error.response.status;
          // Redirect other errors to generic error page
          navigate("/error", {
            state: {
              error: error.response.data || "An error occurred while fetching data",
              status: status
            }
          });
          return;
        } else {
          // Network errors go to generic error page
          navigate("/error", {
            state: {
              error: "Network error. Please check your connection and try again.",
              status: "network"
            }
          });
          return;
        }
      } finally {
        setLoading(false);
      }
    };

    populateExistingData();
  }, [user?.email, location.state?.useDWPData, api, navigate]);

  const onSubmit = async (data: IndividualDto) => {
    setSubmitting(true);
    try {
      await api.post("/api/onboarding/Individual", data);
      navigate("/");
      setTimeout(() => window.location.reload(), 0);
    } catch (error: any) {
      console.error("Error submitting individual data:", error);
      setError(error.response?.data || "An error occurred while submitting the form. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-center">Loading onboarding data...</p>
        </div>
      </div>
    );
  }

  if (notFoundError) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Data Not Found</h1>
            <p className="mb-6">{notFoundError}</p>

            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full btn-primary"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate("/")}
                className="w-full btn-secondary"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="max-w-6xl mx-auto">
      <UnsavedChangesGuard hasUnsavedChanges={isDirty}></UnsavedChangesGuard>
      <form onSubmit={handleSubmit(onSubmit)}>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          {typeof error === "string" ? (
            error
          ) : error && typeof error === "object" && "errors" in error ? (
            Object.entries((error as any).errors).map(([field, messages]: [string, any]) => (
              <div key={field}>
                <strong>{field}:</strong> {Array.isArray(messages) ? messages.join(", ") : String(messages)}
              </div>
            ))
          ) : (
            JSON.stringify(error)
          )}
        </div>
      )}

        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">Individual Information</h1>
              <p className="text-lg">Please review the information below and confirm if it's correct</p>
            </div>
          </div>

          {/* Personal Information Card */}
          {individual && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold">Personal Information</h2>
              </div>

              <div className="space-y-4">
                {/* Basic Info */}
                <h3 className="text-lg font-medium border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormInput label="First Name" name="firstName" register={register} errors={errors} defaultValue={individual.firstName} />
                  <FormInput label="Middle Name" name="middleName" register={register} errors={errors} defaultValue={individual.middleName} />
                  <FormInput label="Last Name" name="lastName" register={register} errors={errors} defaultValue={individual.lastName} />
                  <FormInput label="Suffix" name="nameSuffix" register={register} errors={errors} defaultValue={individual.nameSuffix} />
                  <FormInput label="Date of Birth" name="dateOfBirth" type="date" register={register} errors={errors} defaultValue={individual.dateOfBirth} />
                  <FormInput label="SSN" name="ssn" register={register} errors={errors} defaultValue={individual.ssn} />
                </div>

                {/* Contact Info */}
                <h3 className="text-lg font-medium border-b pb-2">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormInput label="Email" name="email" type="email" register={register} errors={errors} defaultValue={individual.email} />
                  <FormInput label="Phone Number" name="phoneNumber" type="tel" register={register} errors={errors} defaultValue={individual.phoneNumber} />
                  <FormInput label="Country Code" name="countryCode" register={register} errors={errors} defaultValue={individual.countryCode} />
                </div>

                {/* Employment Info */}
                <h3 className="text-lg font-medium border-b pb-2">Employment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Controller
                    control={control}
                    name="employmentStatus"
                    render={({ field }) => (
                      <FormSelect
                        label="Employment Status"
                        value={field.value || null}
                        options={[
                          { value: "Employed", label: "Employed" },
                          { value: "Not Employed", label: "Not Employed" },
                          { value: "Retired", label: "Retired" },
                        ]}
                        onChange={(val) => {
                          const next = (val as string) ?? '';
                          setEmploymentStatus(next);
                          field.onChange(next);
                        }}
                        placeholder="Select Employment Status"
                        isSearchable={false}
                        error={errors.employmentStatus?.message}
                      />
                    )}
                  />

                  {employmentStatus === "Employed" && (
                    <>
                      <FormInput label="Occupation" name="occupation" register={register} errors={errors} defaultValue={individual.occupation} />
                      <FormInput label="Employer Name" name="employerName" register={register} errors={errors} defaultValue={individual.employerName} />
                    </>
                  )}

                  {(employmentStatus === "Not Employed" || employmentStatus === "Retired") && (
                    <Controller
                      control={control}
                      name="incomeSource"
                      render={({ field }) => (
                        <FormSelect
                          label="Income Source"
                          value={field.value || null}
                          options={[
                            { value: "Salary", label: "Salary" },
                            { value: "Investments", label: "Investments" },
                            { value: "Social Security", label: "Social Security" },
                            { value: "Other", label: "Other" },
                          ]}
                          onChange={(val) => field.onChange((val as string) ?? '')}
                          placeholder="Select Income Source"
                          error={errors.incomeSource?.message}
                        />
                      )}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Employer Address Card */}
          {individual && showEmployerInfo && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Employer Address</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Address Line 1" name="employerAddressLine1" register={register} errors={errors} defaultValue={individual.employerAddressLine1} />
                <FormInput label="Address Line 2" name="employerAddressLine2" register={register} errors={errors} defaultValue={individual.employerAddressLine2} />
                <FormInput label="City" name="employerAddressCity" register={register} errors={errors} defaultValue={individual.employerAddressCity} />
                <FormInput label="State" name="employerAddressState" register={register} errors={errors} defaultValue={individual.employerAddressState} />
                <FormInput label="Zip Code" name="employerAddressZipCode" register={register} errors={errors} defaultValue={individual.employerAddressZipCode} />
                <FormInput label="Country" name="employerAddressCountry" register={register} errors={errors} defaultValue={individual.employerAddressCountry} />
              </div>
            </div>
          )}

          {/* Personal Address Card */}
          {individual && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Personal Address</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Address Line 1" name="addressLine1" register={register} errors={errors} defaultValue={individual.addressLine1} />
                <FormInput label="Address Line 2" name="addressLine2" register={register} errors={errors} defaultValue={individual.addressLine2} />
                <FormInput label="City" name="addressCity" register={register} errors={errors} defaultValue={individual.addressCity} />
                <FormInput label="State" name="addressState" register={register} errors={errors} defaultValue={individual.addressState} />
                <FormInput label="Zip Code" name="addressZipCode" register={register} errors={errors} defaultValue={individual.addressZipCode} />
                <FormInput label="Country" name="addressCountry" register={register} errors={errors} defaultValue={individual.addressCountry} />
              </div>
            </div>
          )}

          {/* Additional Information Card */}
          {individual && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Additional Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormInput label="Marital Status" name="maritalStatus" register={register} errors={errors} defaultValue={individual.maritalStatus} />
                <FormInput label="Dependents" name="dependents" type="number" register={register} errors={errors} defaultValue={individual.dependents?.toString()} />
                <FormInput label="Taxpayer Country" name="taxpayerCountry" register={register} errors={errors} defaultValue={individual.taxpayerCountry} />
              </div>
            </div>
          )}

          {/* Confirmation Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold mb-3">Complete Onboarding</h3>
            <p className="mb-4">Please review the information above. If everything looks correct, click Complete to proceed.</p>
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => navigate("/onboarding/agreement")}
                disabled={submitting}
                className="btn-secondary"
              >
                Go Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary"
              >
                {submitting ? (
                  <>
                    <div className={css.spinner}></div>
                    Completeing...
                  </>
                ) : (
                  'Complete'
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OnboardingIndividual;
